/**
 * Function-tool dispatcher.
 *
 * When the Foundry agent uses *function tools* (rather than the
 * apihub-wrapped MCP tool), the run pauses with a `requires_action`
 * status carrying a list of `function_call` items. The SPA executes
 * each call and posts the results back as `function_call_output`
 * items. This module is the "execute" half — it maps a tool name +
 * arguments JSON to an Orion API call and returns the result JSON the
 * agent will see.
 *
 * **Why this lives in the SPA, not the MCP server.** The MCP server's
 * job is to expose Orion tools to *external* clients (Claude.ai) over
 * the MCP protocol, doing OBO from the caller's MCP-scoped token to an
 * API-scoped token. The SPA already has an API-scoped token directly
 * (the same one the rest of the UI uses for /api/initiatives etc.), so
 * routing function-tool calls through the MCP server would only add a
 * stateful session, a JSON-RPC client, and an extra hop with no
 * security benefit. The audit chain is preserved either way — the API
 * audits based on the bearer's `oid`.
 *
 * **Schema parity.** The function-tool argument shapes are
 * intentionally identical to the MCP tool argsSchemas in
 * `mcp/src/tools/<toolName>.ts`. The export script in
 * `mcp/scripts/export-foundry-schemas.mjs` emits the agent's tool
 * definitions straight from those zod schemas, so the agent config
 * and this dispatcher stay in sync by construction. **Adding or
 * renaming a tool requires updating both this file AND running the
 * export script.**
 */

import { apiFetch } from '@/api/client'

// ---------------------------------------------------------------------------
// Public type — what `useFoundryChat` hands to the dispatcher.
// ---------------------------------------------------------------------------

export interface PendingFunctionCall {
  /** Foundry's `call_id` — needed verbatim on the function_call_output. */
  callId: string
  /** Tool name, e.g. `list_initiatives`. */
  name: string
  /** Raw arguments JSON string streamed by the model. */
  argumentsJson: string
}

export interface DispatchedToolResult {
  callId: string
  /**
   * JSON string the agent will see as the tool result. On success this
   * is `JSON.stringify(apiResponse)`; on failure it's a small error
   * envelope so the model can decide whether to retry or apologise to
   * the user.
   */
  output: string
  /** Convenience flag for the UI ("red row" vs "green row"). */
  ok: boolean
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

/**
 * Execute one function-tool call against the Orion API. Always returns
 * a JSON string — never throws — so the caller can post the result
 * back to Foundry regardless of whether the underlying call succeeded.
 * Errors are surfaced via `{ ok: false }` and an `output` envelope.
 */
export async function dispatchToolCall(
  call: PendingFunctionCall,
  getToken: () => Promise<string>,
): Promise<DispatchedToolResult> {
  let args: Record<string, unknown>
  try {
    args = call.argumentsJson.trim() === '' ? {} : JSON.parse(call.argumentsJson)
    if (typeof args !== 'object' || args === null || Array.isArray(args)) {
      throw new Error('arguments must be a JSON object')
    }
  } catch (err) {
    return errorResult(call.callId, 'INVALID_ARGUMENTS', String(err))
  }

  try {
    const result = await dispatchByName(call.name, args, getToken)
    return { callId: call.callId, output: JSON.stringify(result ?? null), ok: true }
  } catch (err) {
    if (isApiError(err)) {
      const envelope = {
        error: {
          code: err.code,
          message: err.message,
          traceId: err.traceId,
          status: err.status,
          details: err.details,
        },
      }
      return { callId: call.callId, output: JSON.stringify(envelope), ok: false }
    }
    return errorResult(
      call.callId,
      'DISPATCH_FAILED',
      err instanceof Error ? err.message : String(err),
    )
  }
}

function errorResult(callId: string, code: string, message: string): DispatchedToolResult {
  return {
    callId,
    output: JSON.stringify({ error: { code, message } }),
    ok: false,
  }
}

function isApiError(err: unknown): err is {
  code: string
  message: string
  traceId: string
  status: number
  details?: unknown
} {
  return (
    typeof err === 'object' && err !== null && 'code' in err && 'status' in err && 'traceId' in err
  )
}

// ---------------------------------------------------------------------------
// Per-tool routing — mirrors mcp/src/tools/*.ts
// ---------------------------------------------------------------------------

/**
 * `list_reference_data` kinds → lookup routes. `statuses` is absent: it
 * retired with `ref.InitiativeStatus` in migration 002, and aliasing it to
 * `stages` would hand the model six codes it did not ask for.
 */
const REFERENCE_DATA_PATHS: Record<string, string> = {
  stages: '/api/lookup/stages',
  businessUnits: '/api/lookup/business-units',
  blockedReasons: '/api/lookup/blocked-reasons',
  aiActClasses: '/api/lookup/ai-act-classes',
  domains: '/api/lookup/domains',
  capabilities: '/api/lookup/capabilities',
}

async function dispatchByName(
  name: string,
  args: Record<string, unknown>,
  getToken: () => Promise<string>,
): Promise<unknown> {
  switch (name) {
    case 'list_initiatives': {
      const qs = buildQuery({
        q: args.q,
        stageCode: args.stageCode,
        lifecycle: args.lifecycle,
        businessUnitCode: args.businessUnitCode,
        blockedReason: args.blockedReason,
        aiActClass: args.aiActClass,
        ownerObjectId: args.ownerObjectId,
        domainId: args.domainId,
        capabilityId: args.capabilityId,
        includeInactive: args.includeInactive,
        page: args.page,
        pageSize: args.pageSize,
      })
      return apiFetch(`/api/initiatives${qs}`, getToken)
    }
    case 'get_initiative': {
      const id = requireNumber(args.initiativeId, 'initiativeId')
      const qs = buildQuery({
        activityLimit: args.activityLimit,
        includeInactive: args.includeInactive,
        asOf: args.asOf,
      })
      return apiFetch(`/api/initiatives/${id}${qs}`, getToken)
    }
    case 'get_gate_readiness': {
      const id = requireNumber(args.initiativeId, 'initiativeId')
      return apiFetch(`/api/initiatives/${id}/gates`, getToken)
    }
    case 'add_comment': {
      const id = requireNumber(args.initiativeId, 'initiativeId')
      return apiFetch(`/api/initiatives/${id}/comments`, getToken, {
        method: 'POST',
        body: JSON.stringify({ body: args.body }),
      })
    }
    case 'create_initiative': {
      return apiFetch(`/api/initiatives`, getToken, {
        method: 'POST',
        body: JSON.stringify(args),
      })
    }
    case 'update_initiative': {
      const id = requireNumber(args.initiativeId, 'initiativeId')
      // The API expects the patch body WITHOUT initiativeId (it's in the path).
      const { initiativeId: _omit, ...rest } = args
      void _omit
      return apiFetch(`/api/initiatives/${id}`, getToken, {
        method: 'PATCH',
        body: JSON.stringify(rest),
      })
    }
    case 'delete_initiative': {
      const id = requireNumber(args.initiativeId, 'initiativeId')
      await apiFetch<void>(`/api/initiatives/${id}`, getToken, { method: 'DELETE' })
      // Match the MCP server's contract — a small object the model can
      // read back, rather than the 204 No Content the API returns.
      return { deactivated: true, initiativeId: id }
    }
    case 'list_capabilities': {
      const qs = buildQuery({
        q: args.q,
        domainId: args.domainId,
        parentCapabilityId: args.parentCapabilityId,
        level: args.level,
        includeInactive: args.includeInactive,
        page: args.page,
        pageSize: args.pageSize,
      })
      return apiFetch(`/api/capabilities${qs}`, getToken)
    }
    case 'get_capability': {
      const id = requireNumber(args.capabilityId, 'capabilityId')
      const qs = buildQuery({ includeInactive: args.includeInactive, asOf: args.asOf })
      return apiFetch(`/api/capabilities/${id}${qs}`, getToken)
    }
    case 'list_domains': {
      const qs = buildQuery({
        q: args.q,
        includeInactive: args.includeInactive,
        page: args.page,
        pageSize: args.pageSize,
      })
      return apiFetch(`/api/domains${qs}`, getToken)
    }
    case 'get_domain': {
      const id = requireNumber(args.domainId, 'domainId')
      const qs = buildQuery({ includeInactive: args.includeInactive, asOf: args.asOf })
      return apiFetch(`/api/domains/${id}${qs}`, getToken)
    }
    case 'list_reference_data': {
      const kind = args.kind
      if (kind === 'users') {
        const qs = buildQuery({ page: args.page, pageSize: args.pageSize })
        return apiFetch(`/api/lookup/users${qs}`, getToken)
      }
      // Kinds are camelCase on the tool surface and kebab-case on the
      // route, so the mapping is explicit rather than interpolated.
      const path = REFERENCE_DATA_PATHS[String(kind)]
      if (!path) {
        throw new Error(`list_reference_data: unsupported kind "${String(kind)}"`)
      }
      return apiFetch(path, getToken)
    }
    default:
      throw new Error(`Unknown tool "${name}"`)
  }
}

function requireNumber(value: unknown, field: string): number {
  if (typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value > 0) {
    return value
  }
  // Defensive: the agent sometimes serialises numeric ids as strings.
  if (typeof value === 'string' && /^[1-9]\d*$/.test(value)) {
    return Number(value)
  }
  throw new Error(`${field} is required and must be a positive integer`)
}

// ---------------------------------------------------------------------------
// Query-string builder (mirrors mcp/src/api/query.ts)
// ---------------------------------------------------------------------------

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      for (const v of value) {
        if (v !== undefined && v !== null) search.append(key, String(v))
      }
    } else {
      search.append(key, String(value))
    }
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}
